import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Simplified build interface matching what ReportsComponent uses/receives
export interface PipelineBuild {
    buildId: number;
    appId: number;
    composerHost: string;
    status: string; // 'queued', 'success', 'failed', 'running'
    phase: string;  // 'npm_build', 'fastlane', etc.
    version: string;
    os: string;
    dateTime: string;
    pipelineUrl: string;
    pipelineId?: string | number; // GitLab pipeline ID or similar
}

interface PipelineContextType {
    recentBuilds: PipelineBuild[];
    updateRecentBuilds: (builds: PipelineBuild[]) => void;
    getLatestBuildForApp: (composerHost: string, platform?: string) => PipelineBuild | undefined;
}

const PipelineContext = createContext<PipelineContextType | undefined>(undefined);

export const PipelineProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [recentBuilds, setRecentBuilds] = useState<PipelineBuild[]>([]);

    const updateRecentBuilds = (builds: PipelineBuild[]) => {
        // Only update if data actually changed to verify strict equality optimizations downstream
        // But simplistic replacement is fine for now
        setRecentBuilds(builds);
    };

    const getLatestBuildForApp = useCallback((composerHost: string, platform?: string): PipelineBuild | undefined => {
        if (!composerHost) return undefined;

        const host = composerHost.trim().toLowerCase();
        const targetPlatform = platform?.trim().toLowerCase();

        // Find the first matching build (assuming recentBuilds is sorted desc by date)
        // If not sorted, we'd need to sort here, but assuming the source API returns sorted data
        return recentBuilds.find(b => {
            const bHost = (b.composerHost || '').trim().toLowerCase();
            const hostMatch = bHost === host || bHost.includes(host) || host.includes(bHost);

            if (!hostMatch) return false;

            // If platform is specified, enforce strict match
            if (targetPlatform) {
                const bOS = (b.os || '').trim().toLowerCase();
                return bOS === targetPlatform;
            }

            return true;
        });
    }, [recentBuilds]);

    return (
        <PipelineContext.Provider value={{ recentBuilds, updateRecentBuilds, getLatestBuildForApp }}>
            {children}
        </PipelineContext.Provider>
    );
};

export const usePipelineContext = () => {
    const context = useContext(PipelineContext);
    if (context === undefined) {
        throw new Error('usePipelineContext must be used within a PipelineProvider');
    }
    return context;
};
